import Image from 'next/image'
import * as React from 'react'

import { isImageAttachmentMessage } from '@/utils/chat-message-content'
import { hasImgTags, parseAllImgContent } from '@/utils/emoji-parser'

import { MessageType } from '@/types/chat.type'

interface RepliedMessagePreviewProps {
  repliedMessage: {
    id: number
    content: string
    type: MessageType
    sender: {
      id: number
      firstName: string
      lastName: string
      avatar?: string
    }
    fileUrl?: string
    fileName?: string
    fileType?: string
  }
  onScrollToMessage?: (messageId: number) => void
  isOwnMessage?: boolean
}

export const RepliedMessagePreview: React.FC<RepliedMessagePreviewProps> = ({
  repliedMessage,
  onScrollToMessage,
  isOwnMessage = false,
}) => {
  const truncateContent = (content: string, maxLength = 80) => {
    if (content.length <= maxLength) return content
    return content.substring(0, maxLength) + '...'
  }

  const getFileIcon = (type: MessageType) => {
    switch (type) {
      case MessageType.IMAGE:
        return '🖼️'
      case MessageType.FILE:
        return '📎'
      case MessageType.AUDIO:
        return '🎵'
      case MessageType.VIDEO:
        return '🎥'
      default:
        return '📄'
    }
  }

  const handleNavigateToRepliedMessage = (event: React.MouseEvent | React.TouchEvent) => {
    event.preventDefault()
    event.stopPropagation()
    onScrollToMessage?.(repliedMessage.id)
  }

  const renderEmojiContent = (content: string) => {
    if (!hasImgTags(content)) {
      return <span className="text-[13px] leading-snug text-gray-600">{truncateContent(content)}</span>
    }

    const segments = parseAllImgContent(content)
    const displaySegments = segments.slice(0, 3)
    const hasMore = segments.length > 3

    return (
      <div className="flex flex-wrap items-center gap-1">
        {displaySegments.map((segment, index) => {
          if (segment.type === 'text') {
            const text = segment.content.length > 20 ? segment.content.substring(0, 20) + '...' : segment.content
            return (
              <span key={index} className="text-[13px] text-gray-600">
                {text}
              </span>
            )
          }
          if ((segment.type === 'custom-emoji' || segment.type === 'emoji') && segment.imageUrl) {
            return (
              <Image
                key={index}
                src={segment.imageUrl}
                alt={segment.alt || 'Image'}
                width={16}
                height={16}
                className="h-4 w-4 rounded object-cover"
              />
            )
          }
          return null
        })}
        {hasMore && <span className="text-[13px] text-gray-500">...</span>}
      </div>
    )
  }

  const renderQuotedContent = () => {
    if (
      repliedMessage.fileUrl &&
      isImageAttachmentMessage(repliedMessage.type, repliedMessage.fileName, repliedMessage.fileType)
    ) {
      return (
        <div className="flex items-center gap-2">
          <Image
            src={repliedMessage.fileUrl}
            alt="Replied image"
            width={20}
            height={20}
            className="pointer-events-none h-5 w-5 rounded object-cover"
            unoptimized
          />
          <span className="text-[13px] text-gray-600">Image</span>
        </div>
      )
    }

    if (repliedMessage.type !== MessageType.TEXT) {
      return (
        <div className="flex items-center gap-2">
          <span className="text-sm">{getFileIcon(repliedMessage.type)}</span>
          <span className="text-[13px] text-gray-600">{repliedMessage.fileName || repliedMessage.type}</span>
        </div>
      )
    }

    return renderEmojiContent(repliedMessage.content)
  }

  return (
    <button
      type="button"
      data-reply-quote
      className={`group/quote flex max-w-full min-w-0 cursor-pointer items-end gap-1.5 ${
        isOwnMessage ? 'flex-row-reverse self-end pr-1' : 'self-start pl-1'
      }`}
      onClick={handleNavigateToRepliedMessage}
      onTouchStart={event => event.stopPropagation()}
      title="Click to scroll to original message"
    >
      <span className="flex min-w-0 flex-col">
        <span
          className={`truncate text-[11px] font-medium text-gray-400 ${isOwnMessage ? 'text-right' : 'text-left'}`}
        >
          {repliedMessage.sender.firstName} {repliedMessage.sender.lastName}
        </span>
        <span className="mt-0.5 max-w-full truncate rounded-2xl bg-gray-100 px-3 py-1.5 text-gray-600 transition-colors group-hover/quote:bg-gray-200">
          {renderQuotedContent()}
        </span>
      </span>
    </button>
  )
}

interface ReplyMessageBubbleProps {
  isOwnMessage: boolean
  seriesBubbleClassName: string
  repliedMessage: RepliedMessagePreviewProps['repliedMessage']
  onScrollToMessage?: (messageId: number) => void
  bubbleClassName?: string
  splitBubbles?: boolean
  actionButtons?: React.ReactNode
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
  onTouchStart?: React.TouchEventHandler<HTMLDivElement>
  onTouchMove?: React.TouchEventHandler<HTMLDivElement>
  onTouchEnd?: React.TouchEventHandler<HTMLDivElement>
  onTouchCancel?: React.TouchEventHandler<HTMLDivElement>
}

export const ReplyMessageBubble: React.FC<ReplyMessageBubbleProps> = ({
  isOwnMessage,
  seriesBubbleClassName,
  repliedMessage,
  onScrollToMessage,
  bubbleClassName,
  splitBubbles = false,
  actionButtons,
  children,
  className,
  style,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  onTouchCancel,
}) => {
  const defaultBubbleClassName = isOwnMessage
    ? 'bg-blue-600 text-white'
    : 'border border-gray-200 bg-white text-gray-900'

  const replyBubble = splitBubbles ? (
    <div
      className="w-full min-w-0"
      style={style}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchCancel}
    >
      {children}
    </div>
  ) : (
    <div
      className={`relative inline-flex max-w-full min-w-0 flex-col px-3.5 py-2 text-sm shadow-sm transition-all duration-200 hover:shadow-md ${seriesBubbleClassName} ${bubbleClassName ?? defaultBubbleClassName}`}
      style={style}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onTouchCancel={onTouchCancel}
    >
      {children}
    </div>
  )

  return (
    <div className={`flex w-full min-w-0 flex-col gap-1 ${isOwnMessage ? 'items-end' : 'items-start'} ${className ?? ''}`}>
      <RepliedMessagePreview
        repliedMessage={repliedMessage}
        onScrollToMessage={onScrollToMessage}
        isOwnMessage={isOwnMessage}
      />
      {actionButtons ? (
        <div
          className={`flex min-w-0 max-w-full items-center gap-2 ${
            isOwnMessage ? 'flex-row self-end' : 'flex-row self-start'
          }`}
        >
          {isOwnMessage ? (
            <>
              {actionButtons}
              {replyBubble}
            </>
          ) : (
            <>
              {replyBubble}
              {actionButtons}
            </>
          )}
        </div>
      ) : (
        replyBubble
      )}
    </div>
  )
}
